import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Dialog from '../Dialog/Dialog';
import TransitionGroup from '../TransitionGroup/TransitionGroup';

let singleton;

/**
 * `Application` is the main container for your application. It is a singleton and should only be instantiated once per
 * web page.
 *
 * It provides easy access to modal OK, warning, error and change dialogs:
 * ````
 * Application.showOkDialog()
 * Application.showWarningDialog()
 * Application.showErrorDialog()
 * Application.showChangeDialog()
 * ````
 *
 * @property {node} children The content of your application.
 * @property {string} className The class name of your application. Will be appended to "core-application".
 * @property {string} id The ID of your application. Defaults to "application".
 *
 * @see ApplicationHeader, ApplicationHeaderTabs, Dialog
 */
export default class Application extends Component {

  static displayName = 'Application';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
  };

  static defaultProps = {
    id: 'application',
  };

  // kept for backwards compatibility reasons. use showOKDialog(), showWarningDialog() and showErrorDialog() instead
  static showDialog(title, message, okText, cancelText, okCallback, cancelCallback) {
    Application.showWarningDialog(title, message, okText, cancelText, null, okCallback, cancelCallback);
  }

  static showInfoDialog(title, message, okText, okCallback, children, okDisabled) {
    // TODO replace singleton with an application bus
    if (singleton) {
      singleton.setState({
        dialogType: Dialog.TYPE_INFO,
        dialogOpen: true,
        dialogTitle: title,
        dialogMessage: message,
        dialogOkText: okText,
        dialogCancelText: null,
        dialogOkCallback: okCallback,
        dialogCancelCallback: null,
        dialogDiscardText: null,
        children,
        dialogOkDisabled: okDisabled
      });
    } else {
      console.error('Application singleton not initialized');
    }
  }

  static showOkDialog(title, message, okText, okCallback) {
    // TODO replace singleton with an application bus
    if (singleton) {
      singleton.setState({
        dialogType: Dialog.TYPE_OK,
        dialogOpen: true,
        dialogTitle: title,
        dialogMessage: message,
        dialogOkText: okText,
        dialogCancelText: null,
        dialogOkCallback: okCallback,
        dialogCancelCallback: null,
        dialogDiscardText: null
      });
    } else {
      console.error('Application singleton not initialized');
    }
  }

  static showWarningDialog(title, message, okText, cancelText, discardText = null,
    okCallback, cancelCallback, discardCallback, okDisabled, discardDisabled) {
    // TODO replace singleton with an application bus
    if (singleton) {
      singleton.setState({
        dialogType: Dialog.TYPE_WARNING,
        dialogOpen: true,
        dialogTitle: title,
        dialogMessage: message,
        dialogOkText: okText,
        dialogCancelText: cancelText,
        dialogDiscardText: discardText,
        dialogOkCallback: okCallback,
        dialogCancelCallback: cancelCallback,
        dialogDiscardCallback: discardCallback,
        dialogDiscardDisabled: discardDisabled,
        dialogOkDisabled: okDisabled
      });
    } else {
      console.error('Application singleton not initialized');
    }
  }

  static showChangeDialog(title, message, okText, cancelText,
    discardText, okCallback, cancelCallback, discardCallback, okDisabled, discardDisabled) {
    Application.showWarningDialog(title, message, okText, cancelText,
      discardText, okCallback, cancelCallback, discardCallback, okDisabled, discardDisabled);
  }

  static showErrorDialog(title, message, okText, cancelText, okCallback, cancelCallback, okDisabled) {
    // TODO replace singleton with an application bus
    if (singleton) {
      singleton.setState({
        dialogType: Dialog.TYPE_ERROR,
        dialogOpen: true,
        dialogTitle: title,
        dialogMessage: message,
        dialogOkText: okText,
        dialogCancelText: cancelText,
        dialogOkCallback: okCallback,
        dialogCancelCallback: cancelCallback,
        dialogDiscardText: null,
        dialogOkDisabled: okDisabled
      });
    } else {
      console.error('Application singleton not initialized');
    }
  }

  constructor(props, context) {
    super(props, context);
    singleton = this;
    this.onOK = this.onOK.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onDiscard = this.onDiscard.bind(this);
    this.state = {};
  }

  onOK() {
    this.setState({
      dialogOpen: false
    });
    if (this.state.dialogOkCallback) {
      this.state.dialogOkCallback();
    }
  }

  onCancel() {
    this.setState({
      dialogOpen: false
    });
    if (this.state.dialogCancelCallback) {
      this.state.dialogCancelCallback();
    }
  }

  onDiscard() {
    this.setState({
      dialogOpen: false
    });
    if (this.state.dialogDiscardCallback) {
      this.state.dialogDiscardCallback();
    }
  }

  renderDialog = () => {
    const { dialogCancelText, dialogDiscardText, dialogMessage, dialogOkText, dialogTitle, dialogType, dialogDiscardDisabled, dialogOkDisabled, children: dialogChildren } = this.state;

    return (
      <Dialog type={dialogType} title={dialogTitle} message={dialogMessage} ok={dialogOkText}
        cancel={dialogCancelText} okCallback={this.onOK} cancelCallback={this.onCancel} discard={dialogDiscardText}
        discardCallback={this.onDiscard} discardDisabled={dialogDiscardDisabled} okDisabled={dialogOkDisabled}>
        {dialogChildren}
      </Dialog>
    );
  };

  render() {
    const { id, children, className } = this.props;

    return (
      <div id={id} className={cx('core-application', className)}>
        {children}
        <TransitionGroup
          in={this.state.dialogOpen}
          className="animate-dialog"
          contentRenderer={this.renderDialog}/>
      </div>
    );
  }

}
